/**
 * Freier Anzeigezeitraum der Belegung: Beginn und Ende im Kalender wählen, die Ansicht zeigt genau
 * diese Tage — auch rückwärts. Erster Klick setzt den Beginn, zweiter das Ende, der dritte beginnt
 * neu. Steht im Popover am Desktop und aufgeklappt im Blatt am Handy.
 */
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { de } from 'date-fns/locale/de';
import { Button } from '@/shared/components';
import { DayPickerChevron, dayPickerRangeClassNames } from '@/shared/components/dayPickerTheme';
import { daysInRange } from './calendarDates';

/** Mehr Tage liefert der Server in einem Abruf nicht. */
export const MAX_VIEW_DAYS = 90;

export interface IsoRange {
  from: string;
  to: string;
}

// Der Picker rechnet mit lokalen Kalendertagen, die Ansicht mit ISO-Tagen.
function toLocalDate(iso: string) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, (month ?? 1) - 1, day ?? 1);
}

function toIsoDay(date: Date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

interface Props {
  initial: IsoRange;
  months: 1 | 2;
  /** Beschriftung der Übernahme; im Blatt anders als dessen eigenes „Anzeigen“. */
  applyLabel?: string;
  onApply: (range: IsoRange) => void;
  onCancel: () => void;
}

export function ViewRangeCalendar({
  initial,
  months,
  applyLabel = 'Anzeigen',
  onApply,
  onCancel,
}: Props) {
  const [range, setRange] = useState<IsoRange>(initial);
  const [anchor, setAnchor] = useState<string | null>(null);
  const days = daysInRange(range.from, range.to);
  const tooLong = days > MAX_VIEW_DAYS;

  const pick = (day: string) => {
    if (anchor === null) {
      setAnchor(day);
      setRange({ from: day, to: day });
      return;
    }
    setRange(day < anchor ? { from: day, to: anchor } : { from: anchor, to: day });
    setAnchor(null);
  };

  return (
    <div className="space-y-4">
      <DayPicker
        mode="range"
        locale={de}
        weekStartsOn={1}
        numberOfMonths={months}
        defaultMonth={toLocalDate(initial.from)}
        selected={{ from: toLocalDate(range.from), to: toLocalDate(range.to) }}
        onSelect={(_, triggerDate) => pick(toIsoDay(triggerDate))}
        classNames={dayPickerRangeClassNames}
        components={{ Chevron: DayPickerChevron }}
      />
      <div className="border-line/50 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
        <p className="text-ink-soft text-[13px]" aria-live="polite">
          {anchor !== null
            ? 'Ende wählen'
            : tooLong
              ? `Höchstens ${MAX_VIEW_DAYS} Tage auf einmal`
              : `${days} ${days === 1 ? 'Tag' : 'Tage'}`}
        </p>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="ghost"
            className="border-line bg-card min-h-11 border"
            onClick={onCancel}
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="brand"
            className="min-h-11"
            disabled={anchor !== null || tooLong}
            onClick={() => onApply(range)}
          >
            {applyLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
